// Pulse.js
import React, { useState, useEffect, useRef } from 'react';
import { fetchWithAuth } from './apiClient';
import { Box, useToast } from '@chakra-ui/react';
import { motion, AnimatePresence } from 'framer-motion';
import EmptyState from './components/EmptyState';
// import { requestFirebaseMessagingPermission, onMessageListener } from './firebaseConfig';
import ScheduleTimeline from './ScheduleTimeline';
import ChatBar from './ChatBar';
import { useSchedules } from './query/useSchedules';
import SchedulesSkeleton from './components/SchedulesSkeleton';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import OnboardingCoach from './components/OnboardingCoach';
import { useServiceStatus } from './query/useServiceStatus';
import SchedulingProgressCard from './components/SchedulingProgressCard';
import { useTrackEvent } from './analytics';

const MotionBox = motion(Box);
const SCHEDULING_STEPS = [
  { key: 'parse', label: 'Understanding your request' },
  { key: 'plan', label: 'Checking schedule details' },
  { key: 'save', label: 'Saving to Ekkos' },
];

const Pulse = () => {
  const track = useTrackEvent();
  const [inputText, setInputText] = useState('');
  const [editingTarget, setEditingTarget] = useState(null); // { originJobKey, description }
  const [completingTarget, setCompletingTarget] = useState(null); // { originJobKey, description }
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionMode, setSubmissionMode] = useState(null); // create | edit | complete
  const [progress, setProgress] = useState(null);
  const progressTimersRef = useRef([]);
  const [coachSubmissionSignal, setCoachSubmissionSignal] = useState(null);
  const toast = useToast();
  const qc = useQueryClient();
  const schedulesQuery = useSchedules();
  const serviceStatus = useServiceStatus();
  const automationDisabled = serviceStatus.data?.automationEnabled === false;
  const recoveryMessage = serviceStatus.data?.message || 'Ekkos is in recovery mode.';

  // Create schedule mutation (optimistic insert)
  const createScheduleMutation = useMutation({
    mutationFn: async (reminder) => {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const endpoint = `/Scheduler/schedule?reminder=${encodeURIComponent(reminder)}&timezone=${encodeURIComponent(timezone)}`;
      await fetchWithAuth(endpoint, { method: 'POST' });
      // Assume server will include it on next refetch
    },
    onMutate: async (reminder) => {
      await qc.cancelQueries({ queryKey: ['schedules'] });
      const prev = qc.getQueryData(['schedules']);
      const tempId = `temp-${Date.now()}`;
      qc.setQueryData(['schedules'], (old = []) => [
        {
          originJobKey: tempId,
          description: reminder,
          // Ensure draft never looks like it's firing before server responds
          nextRunTime: null,
          dueNow: false,
          isFiring: false,
          createdDate: new Date().toISOString(),
          naturalCronDescription: null,
          analytics: null,
          __optimistic: true,
          isSaving: true,
        },
        ...old,
      ]);
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) qc.setQueryData(['schedules'], ctx.prev);
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['schedules'] });
    },
  });

  useEffect(() => {
    if ('Notification' in window) {
      Notification.requestPermission();
    }
  // Expose a global setter for quick prop-less bubbling from the timeline
  // (ScheduleTimeline/ScheduleItem will call window.setGlobalEditingTarget(item))
  // This keeps edits minimal and avoids deep prop threading in this patch.
  window.setGlobalEditingTarget = function(it) {
      setEditingTarget(it);
      // scroll composer into view and focus after a frame
      requestAnimationFrame(() => {
        try {
          document.getElementById('composer')?.scrollIntoView({ behavior: 'smooth', block: 'end' });
          const el = document.getElementById('composer-input');
          if (el && typeof el.focus === 'function') el.focus();
        } catch (e) { }
      });
  };
  // new: global complete setter — same focusing behavior as edit
  window.setGlobalCompletingTarget = function(it) {
      setCompletingTarget(it);
      requestAnimationFrame(() => {
        try {
          document.getElementById('composer')?.scrollIntoView({ behavior: 'smooth', block: 'end' });
          const el = document.getElementById('composer-input');
          if (el && typeof el.focus === 'function') el.focus();
        } catch (e) {}
      });
  };
  return () => { try { delete window.setGlobalEditingTarget; } catch { } };
  }, [toast]);

  useEffect(() => {
    return () => { try { delete window.setGlobalCompletingTarget; } catch {} };
  }, []);

  // When editingTarget changes, focus composer and add key handler for Esc
  useEffect(() => {
    if (editingTarget) {
      requestAnimationFrame(() => {
        document.getElementById('composer')?.scrollIntoView({ behavior: 'smooth', block: 'end' });
        const el = document.getElementById('composer-input');
        if (el) el.focus();
      });
    }
  }, [editingTarget]);

  const clearProgressTimers = () => {
    progressTimersRef.current.forEach((t) => clearTimeout(t));
    progressTimersRef.current = [];
  };

  const advanceProgress = (stepIndex) => {
    setProgress((prev) => {
      if (!prev || prev.state !== 'inflight') return prev;
      return { ...prev, stepIndex: Math.max(prev.stepIndex, stepIndex) };
    });
  };

  const startProgress = (text) => {
    clearProgressTimers();
    setProgress({
      id: Date.now(),
      text,
      stepIndex: 0,
      state: 'inflight',
      errorMessage: null,
    });
    // stage gentle step progress while waiting
    const timers = [
      setTimeout(() => advanceProgress(1), 900),
      setTimeout(() => advanceProgress(2), 2200),
    ];
    progressTimersRef.current = timers;
  };

  const finishProgress = (state, errorMessage) => {
    clearProgressTimers();
    setProgress((prev) => {
      if (!prev) return prev;
      const lastIndex = SCHEDULING_STEPS.length - 1;
      return {
        ...prev,
        stepIndex: Math.max(prev.stepIndex, lastIndex),
        state,
        errorMessage: errorMessage || null,
      };
    });
    if (state === 'done') {
      setTimeout(() => setProgress(null), 1400);
    }
  };

  useEffect(() => {
    return () => {
      clearProgressTimers();
    };
  }, []);

  const flashRow = (originJobKey) => {
    const el = document.querySelector(`[data-job="${originJobKey}"]`);
    if (!el) return;
    el.classList.add('ek-flash');
    setTimeout(() => el.classList.remove('ek-flash'), 1000);
  };

  const handleChange = (event) => setInputText(event.target.value);

  const handleSubmit = async () => {
    const mode = editingTarget ? 'edit' : (completingTarget ? 'complete' : 'create');
    let zeroTasksBeforeSubmit = null;
    if (automationDisabled && !completingTarget) {
      toast({
        title: 'Scheduling paused',
        description: recoveryMessage,
        status: 'warning',
        duration: 6000,
        isClosable: true,
      });
      return;
    }

    track('schedule_submit', { mode });
    setSubmissionMode(mode);
    setIsSubmitting(true);
    if (mode === 'create') {
      // prevent further edits while create flow runs
      setInputText((text) => text);
    }
    try {
      if (editingTarget) {
        // update flow with inline progress
        const targetKey = editingTarget.originJobKey;
        track('schedule_edit_start', { mode });
        startProgress(inputText || editingTarget.description);
        await qc.cancelQueries({ queryKey: ['schedules'] });
        const prev = qc.getQueryData(['schedules']);
        qc.setQueryData(['schedules'], (old = []) =>
          old.map((x) => (x.originJobKey === targetKey ? { ...x, description: inputText } : x))
        );
        try {
          const { updateSchedule } = await import('./services/scheduleService');
          await updateSchedule(editingTarget.originJobKey, inputText, toast);
          finishProgress('done');
          track('schedule_edit_success', { mode });
        } catch (e) {
          if (prev) qc.setQueryData(['schedules'], prev);
          finishProgress('error', e?.message || 'Failed to update the task.');
          track('schedule_edit_error', { mode });
          toast({
            title: 'Error updating task',
            description: e?.message || 'Failed to update the task.',
            status: 'error',
            duration: 9000,
            isClosable: true,
            position: 'top-right',
          });
        } finally {
          qc.invalidateQueries({ queryKey: ['schedules'] });
        }
        setEditingTarget(null);
        setInputText('');
        setIsSubmitting(false);
        setSubmissionMode(null);
        return;
      }

      if (completingTarget) {
        // Optimistic complete: remove from list
        const key = completingTarget.originJobKey;
        track('schedule_complete_start', { mode });
        await qc.cancelQueries({ queryKey: ['schedules'] });
        const prev = qc.getQueryData(['schedules']);
        qc.setQueryData(['schedules'], (old = []) => old.filter(x => x.originJobKey !== key));
        try {
          const { completeSchedule } = await import('./services/scheduleService');
          await completeSchedule(completingTarget.originJobKey, toast, undefined, inputText);
          track('schedule_complete_success', { mode });
        } catch (e) {
          if (prev) qc.setQueryData(['schedules'], prev);
          throw e;
        } finally {
          qc.invalidateQueries({ queryKey: ['schedules'] });
        }
        setCompletingTarget(null);
        setInputText('');
        setIsSubmitting(false);
        return;
      }

      // create flow
      zeroTasksBeforeSubmit = (schedulesQuery.data?.length || 0) === 0;
      track('schedule_create_start', { mode, zero_tasks_before_submit: zeroTasksBeforeSubmit });
      startProgress(inputText);
      await createScheduleMutation.mutateAsync(inputText);
      setCoachSubmissionSignal({
        id: Date.now() + Math.random(),
        zeroTasksBeforeSubmit,
      });
      setInputText('');
      finishProgress('done');
      track('schedule_create_success', { zero_tasks_before_submit: zeroTasksBeforeSubmit });
    } catch (error) {
      console.error('Error:', error);
      const errorMessage = error?.message || 'Failed to schedule the task.';
      track('schedule_submit_error', { mode });
      if (mode === 'create') {
        track('schedule_create_error', { zero_tasks_before_submit: zeroTasksBeforeSubmit });
      } else if (mode === 'complete') {
        track('schedule_complete_error', { mode });
      }
      if (mode === 'create') {
        finishProgress('error', errorMessage);
      } else {
        toast({
          title: 'Error',
          description: errorMessage,
          status: 'error',
          duration: 9000,
          isClosable: true,
          position: 'top-right',
        });
      }
    } finally {
      setIsSubmitting(false);
      setSubmissionMode(null);
    }
  };

  return (
    <MotionBox
      display="flex"
      flexDirection="column"
      flex="1"
      height="100vh"
      // Ensures we control scrolling ourselves
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
    >
      {/* Scrollable timeline region */}
      <Box position="relative" flex="1" overflowX="hidden">
        {progress ? (
          <Box
            position="absolute"
            inset={0}
            bg="rgba(0,0,0,0.18)"
            backdropFilter="blur(3px)"
            zIndex={5}
            pointerEvents="none"
          />
        ) : null}
        <Box
          flex="1"
          overflowY="auto"
          overflowX="hidden"
          w="100%"
          maxW="800px"
          mx="auto"
          minW="0"
          px={{ base: 4, md: 6 }}
          boxSizing="border-box"
          position="relative"
          zIndex={1}
        >
          {schedulesQuery.isLoading && !schedulesQuery.data ? (
            <SchedulesSkeleton />
          ) : (schedulesQuery.data?.length || 0) === 0 ? (
            <EmptyState />
          ) : (
            <ScheduleTimeline
              schedules={schedulesQuery.data || []}
              refreshSchedules={() => qc.invalidateQueries({ queryKey: ['schedules'] })}
              editingTarget={editingTarget}
              completingTarget={completingTarget}
              automationDisabled={automationDisabled}
              recoveryMessage={recoveryMessage}
            />
          )}
        </Box>
      </Box>
      <Box
        position="sticky"
        bottom="0"
        w="100%"
        minW="0"
        maxW="800px"
        mx="auto"
        px={{ base: 4, md: 6 }}
        py={{ base: 4, md: 6 }}
        flexShrink={0}
        bg="transparent"
        zIndex={20}
        boxSizing="border-box"
      >
        <AnimatePresence>
          {progress ? (
            <Box mb={4} as={motion.div} layout key={progress.id}>
              <SchedulingProgressCard
                steps={SCHEDULING_STEPS}
                progress={progress}
                onClose={() => {
                  clearProgressTimers();
                  setProgress(null);
                }}
              />
            </Box>
          ) : null}
        </AnimatePresence>
        <ChatBar
          id="composer"
          inputId="command-input"
          addButtonId="command-add"
          value={inputText}
          onChange={setInputText}
          onSubmit={async () => {
            await handleSubmit();
            // after successful update, if there was an editing target, flash its row
            if (editingTarget) {
              flashRow(editingTarget.originJobKey);
            }
          }}
          isSubmitting={isSubmitting}
          editingTarget={editingTarget}
          onCancelEdit={() => setEditingTarget(null)}
          completingTarget={completingTarget}
          onCancelComplete={() => setCompletingTarget(null)}
          schedulingDisabled={automationDisabled}
          disabledMessage={recoveryMessage}
          submissionMode={submissionMode || 'create'}
        />
        <OnboardingCoach
          inputValue={inputText}
          onInsertSuggestion={(phrase) => setInputText(phrase)}
          submissionSignal={coachSubmissionSignal}
          composerInputId="command-input"
          addButtonId="command-add"
        />
      </Box>
    </MotionBox>
  );
};

export default Pulse;
